import { keyframes } from '@emotion/react';
import type {
  ButtonProps,
  ComponentIconProp,
  StackProps,
} from '@hover/blueprint';
import { Button, HStack, IconButton, forwardRef } from '@hover/blueprint';
import { iInfo, iShare } from '@hover/icons';
import React from 'react';

import { TranslationNamespace, useTranslate } from 'src/shared/hooks';
import { buttonPressed } from 'src/shared/telemetry';

import { useHomeownerAppContext, useShareGeneratedImage } from '../../hooks';
import type { AiOrSampleDisplayImage } from '../../types';
import { isSample } from '../../types';

import { layoutBreakpoint } from './helpers';

interface MetaButtonProps extends Omit<ButtonProps, 'shape'> {
  icon: ComponentIconProp;
  label: string;
}

const MetaButton = forwardRef<MetaButtonProps, 'button'>(
  ({ icon, label, ...props }, ref) => (
    <>
      <Button
        colorScheme="light"
        display={{ base: 'none', [layoutBreakpoint]: 'flex' }}
        variant="tertiary"
        {...props}
        ref={ref}
      >
        {label}
      </Button>
      <IconButton
        colorScheme="light"
        display={{ base: 'flex', [layoutBreakpoint]: 'none' }}
        icon={icon}
        iconSize="medium"
        label={label}
        size="small"
        variant="ghost"
        {...props}
      />
    </>
  ),
);

interface MetaButtonsProps extends StackProps {
  image: AiOrSampleDisplayImage;
  onDesignDetailsClick: () => void;
}

const fadeInAnimation = keyframes`
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
`;

const MetaButtons = forwardRef<MetaButtonsProps, 'div'>(
  ({ image, onDesignDetailsClick, ...props }, ref) => {
    const t = useTranslate(TranslationNamespace.HOMEOWNER_APP);
    const { trackingData } = useHomeownerAppContext();

    const handleShare = useShareGeneratedImage({
      image: image.aiGeneratedImage,
    });

    const handleShareClick = () => {
      buttonPressed({
        ...trackingData,
        track_id: 'share-button',
        button_text: 'share-icon',
        ...(isSample(image)
          ? {}
          : {
              ai_generated_image_id: image.aiGeneratedImage.id,
              ai_display_image_id: image.id,
            }),
      });

      handleShare();
    };

    return (
      <HStack
        animation={`${fadeInAnimation} 1s`}
        justifyContent="end"
        spacing={{ base: '100', [layoutBreakpoint]: '300' }}
        {...props}
        ref={ref}
      >
        <MetaButton
          icon={iInfo}
          label={t('fullscreenImage.details')}
          onClick={onDesignDetailsClick}
        />
        <MetaButton
          icon={iShare}
          label={t('thumbnailEngagementButtons.share')}
          onClick={handleShareClick}
        />
      </HStack>
    );
  },
);

export { MetaButtons };
